/**
 * Material-UI Theme Configuration
 *
 * Extends MUI's default theme with custom design tokens.
 * Use this theme with ThemeProvider to apply consistent styling.
 *
 * Usage:
 * import { ThemeProvider } from '@mui/material/styles';
 * import theme from './theme/muiTheme';
 *
 * <ThemeProvider theme={theme}>
 *   <App />
 * </ThemeProvider>
 */

import { createTheme } from '@mui/material/styles';
import { colors, typography, spacing, shadows, radius, transitions, a11y } from './tokens';

const theme = createTheme({
  // Color palette
  palette: {
    mode: 'light',
    primary: {
      main: colors.primary.main,
      dark: colors.primary.dark,
      light: colors.primary.light,
      contrastText: colors.primary.contrast,
    },
    secondary: {
      main: colors.secondary.main,
      dark: colors.secondary.dark,
      light: colors.secondary.light,
      contrastText: colors.secondary.contrast,
    },
    error: {
      main: colors.error.main,
      dark: colors.error.dark,
      light: colors.error.light,
      contrastText: colors.error.contrast,
    },
    warning: {
      main: colors.warning.main,
      dark: colors.warning.dark,
      light: colors.warning.light,
      contrastText: colors.warning.contrast,
    },
    info: {
      main: colors.info.main,
      dark: colors.info.dark,
      light: colors.info.light,
      contrastText: colors.info.contrast,
    },
    success: {
      main: colors.success.main,
      dark: colors.success.dark,
      light: colors.success.light,
      contrastText: colors.success.contrast,
    },
    grey: colors.neutral,
    text: {
      primary: colors.text.primary,
      secondary: colors.text.secondary,
      disabled: colors.text.disabled,
    },
    background: {
      default: colors.background.default,
      paper: colors.background.paper,
    },
    divider: colors.border.main,
  },

  // Typography
  typography: {
    fontFamily: typography.fontFamily.primary,
    fontSize: 16, // Base font size in pixels
    fontWeightLight: typography.fontWeight.light,
    fontWeightRegular: typography.fontWeight.regular,
    fontWeightMedium: typography.fontWeight.medium,
    fontWeightBold: typography.fontWeight.bold,

    h1: {
      fontSize: typography.fontSize['5xl'],
      fontWeight: typography.fontWeight.bold,
      lineHeight: typography.lineHeight.tight,
      letterSpacing: typography.letterSpacing.tight,
    },
    h2: {
      fontSize: typography.fontSize['4xl'],
      fontWeight: typography.fontWeight.bold,
      lineHeight: typography.lineHeight.tight,
      letterSpacing: typography.letterSpacing.tight,
    },
    h3: {
      fontSize: typography.fontSize['3xl'],
      fontWeight: typography.fontWeight.semibold,
      lineHeight: typography.lineHeight.snug,
    },
    h4: {
      fontSize: typography.fontSize['2xl'],
      fontWeight: typography.fontWeight.semibold,
      lineHeight: typography.lineHeight.snug,
    },
    h5: {
      fontSize: typography.fontSize.xl,
      fontWeight: typography.fontWeight.semibold,
      lineHeight: typography.lineHeight.normal,
    },
    h6: {
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.semibold,
      lineHeight: typography.lineHeight.normal,
    },
    body1: {
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.regular,
      lineHeight: typography.lineHeight.normal,
    },
    body2: {
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.regular,
      lineHeight: typography.lineHeight.normal,
    },
    button: {
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.semibold,
      textTransform: 'none', // Don't uppercase buttons by default
      letterSpacing: typography.letterSpacing.normal,
    },
    caption: {
      fontSize: typography.fontSize.xs,
      fontWeight: typography.fontWeight.regular,
      lineHeight: typography.lineHeight.normal,
    },
    overline: {
      fontSize: typography.fontSize.xs,
      fontWeight: typography.fontWeight.medium,
      textTransform: 'uppercase',
      letterSpacing: typography.letterSpacing.wide,
    },
  },

  // Spacing (MUI uses 8px as base unit)
  spacing: 8,

  // Shape (border radius)
  shape: {
    borderRadius: parseInt(radius.md), // 8px default
  },

  // Shadows
  shadows: [
    'none',
    shadows.xs,
    shadows.sm,
    shadows.md,
    shadows.md,
    shadows.lg,
    shadows.lg,
    shadows.xl,
    shadows.xl,
    shadows['2xl'],
    shadows['2xl'],
    shadows['2xl'],
    shadows['2xl'],
    shadows['2xl'],
    shadows['2xl'],
    shadows['2xl'],
    shadows['2xl'],
    shadows['2xl'],
    shadows['2xl'],
    shadows['2xl'],
    shadows['2xl'],
    shadows['2xl'],
    shadows['2xl'],
    shadows['2xl'],
    shadows['2xl'],
  ],

  // Transitions
  transitions: {
    easing: {
      easeInOut: transitions.easing.easeInOut,
      easeOut: transitions.easing.easeOut,
      easeIn: transitions.easing.easeIn,
      sharp: transitions.easing.sharp,
    },
    duration: {
      shortest: parseInt(transitions.duration.fast),
      shorter: parseInt(transitions.duration.base),
      short: parseInt(transitions.duration.slow),
      standard: parseInt(transitions.duration.slow),
      complex: parseInt(transitions.duration.slower),
      enteringScreen: parseInt(transitions.duration.base),
      leavingScreen: parseInt(transitions.duration.fast),
    },
  },

  // Breakpoints
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },

  // Component overrides
  components: {
    // Button
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: radius.button,
          fontWeight: typography.fontWeight.semibold,
          textTransform: 'none',
          transition: transitions.all,
          '&:focus-visible': {
            outline: `${a11y.focusRing.width} ${a11y.focusRing.style} ${a11y.focusRing.color}`,
            outlineOffset: a11y.focusRing.offset,
          },
        },
        sizeSmall: {
          padding: '6px 16px',
          fontSize: typography.fontSize.sm,
          minHeight: a11y.touchTarget.min,
        },
        sizeMedium: {
          padding: '10px 24px',
          fontSize: typography.fontSize.base,
          minHeight: a11y.touchTarget.min,
        },
        sizeLarge: {
          padding: '12px 32px',
          fontSize: typography.fontSize.lg,
          minHeight: a11y.touchTarget.recommended,
        },
        contained: {
          boxShadow: shadows.button,
          '&:hover': {
            // Use filter instead of boxShadow for better performance (GPU-accelerated)
            filter: 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1)) drop-shadow(0 2px 4px rgba(0, 0, 0, 0.06))',
          },
        },
      },
    },

    // TextField / Input
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: radius.input,
            transition: transitions.border,
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.border.dark,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.primary.main,
              borderWidth: '2px',
            },
            '&.Mui-error .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.error.main,
            },
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: colors.primary.main,
          },
          '& .MuiInputLabel-root.Mui-error': {
            color: colors.error.main,
          },
        },
      },
    },

    // Card
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: radius.card,
          boxShadow: shadows.card,
          border: `1px solid ${colors.border.light}`,
          transition: transitions.all,
          '&:hover': {
            // Use filter instead of boxShadow for better performance (GPU-accelerated)
            filter: 'drop-shadow(0 10px 15px rgba(0, 0, 0, 0.1)) drop-shadow(0 4px 6px rgba(0, 0, 0, 0.05))',
          },
        },
      },
    },

    // Paper
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: radius.card,
        },
        rounded: {
          borderRadius: radius.card,
        },
        elevation1: {
          boxShadow: shadows.sm,
        },
        elevation2: {
          boxShadow: shadows.md,
        },
        elevation3: {
          boxShadow: shadows.lg,
        },
      },
    },

    // Dialog / Modal
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: radius.modal,
          boxShadow: shadows.modal,
        },
      },
    },

    // Chip / Badge
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: radius.badge,
          fontWeight: typography.fontWeight.medium,
        },
      },
    },

    // Alert
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: radius.md,
          fontWeight: typography.fontWeight.medium,
          fontSize: typography.fontSize.sm,
        },
        standardError: {
          backgroundColor: colors.error.bg,
          color: colors.error.dark,
          borderLeft: `4px solid ${colors.error.main}`,
        },
        standardWarning: {
          backgroundColor: colors.warning.bg,
          color: colors.warning.dark,
          borderLeft: `4px solid ${colors.warning.main}`,
        },
        standardInfo: {
          backgroundColor: colors.info.bg,
          color: colors.info.dark,
          borderLeft: `4px solid ${colors.info.main}`,
        },
        standardSuccess: {
          backgroundColor: colors.success.bg,
          color: colors.success.dark,
          borderLeft: `4px solid ${colors.success.main}`,
        },
      },
    },

    // Tabs
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: typography.fontWeight.medium,
          fontSize: typography.fontSize.base,
          minHeight: a11y.touchTarget.min,
          transition: transitions.all,
          '&:focus-visible': {
            outline: `${a11y.focusRing.width} ${a11y.focusRing.style} ${a11y.focusRing.color}`,
            outlineOffset: a11y.focusRing.offset,
          },
          '&.Mui-selected': {
            fontWeight: typography.fontWeight.semibold,
          },
        },
      },
    },

    // Link
    MuiLink: {
      styleOverrides: {
        root: {
          color: colors.text.link,
          textDecoration: 'none',
          fontWeight: typography.fontWeight.medium,
          transition: transitions.color,
          '&:hover': {
            textDecoration: 'underline',
            color: colors.primary.dark,
          },
          '&:focus-visible': {
            outline: `${a11y.focusRing.width} ${a11y.focusRing.style} ${a11y.focusRing.color}`,
            outlineOffset: a11y.focusRing.offset,
            borderRadius: radius.sm,
          },
        },
      },
    },

    // IconButton
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: transitions.all,
          minWidth: a11y.touchTarget.min,
          minHeight: a11y.touchTarget.min,
          '&:focus-visible': {
            outline: `${a11y.focusRing.width} ${a11y.focusRing.style} ${a11y.focusRing.color}`,
            outlineOffset: a11y.focusRing.offset,
          },
        },
      },
    },

    // Tooltip
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: colors.slate[900],
          fontSize: typography.fontSize.xs,
          fontWeight: typography.fontWeight.medium,
          borderRadius: radius.md,
          padding: `${spacing.sm} ${spacing.md}`,
          boxShadow: shadows.tooltip,
        },
        arrow: {
          color: colors.slate[900],
        },
      },
    },

    // Snackbar
    MuiSnackbar: {
      styleOverrides: {
        root: {
          '& .MuiAlert-root': {
            borderRadius: radius.lg,
            boxShadow: shadows.modal,
          },
        },
      },
    },

    // AppBar
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: shadows.md,
        },
        colorPrimary: {
          backgroundColor: colors.primary.main,
          color: colors.primary.contrast,
        },
      },
    },

    // Divider
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: colors.border.light,
        },
      },
    },

    // Switch (accessibility: ensure proper touch target)
    MuiSwitch: {
      styleOverrides: {
        root: {
          padding: '9px', // Ensures 44px touch target
        },
      },
    },

    // Checkbox (accessibility: ensure proper touch target)
    MuiCheckbox: {
      styleOverrides: {
        root: {
          padding: '9px', // Ensures 44px touch target
          '&:focus-visible': {
            outline: `${a11y.focusRing.width} ${a11y.focusRing.style} ${a11y.focusRing.color}`,
            outlineOffset: a11y.focusRing.offset,
            borderRadius: radius.sm,
          },
        },
      },
    },

    // Radio (accessibility: ensure proper touch target)
    MuiRadio: {
      styleOverrides: {
        root: {
          padding: '9px', // Ensures 44px touch target
          '&:focus-visible': {
            outline: `${a11y.focusRing.width} ${a11y.focusRing.style} ${a11y.focusRing.color}`,
            outlineOffset: a11y.focusRing.offset,
            borderRadius: radius.full,
          },
        },
      },
    },
  },
});

export default theme;
